// Inquiry Answer (PRODUCT_DEV-635) — 問い合わせへの AI 回答ドラフト生成 jobKind 契約。
//
// 共有エージェントジョブ基盤 (agent-jobs/contract.ts) の上に 1 つの jobKind として実装する
// （feature 専用エンドポイントは作らず、unizo API がサーバサイドで台帳へ enqueue する）。
// idea-review と同型。回答は「ドラフト」であり、運営が Admin 画面で確認・編集して承認するまで
// ユーザには届かない（自動即返信しない）。
//
// ここでは AgentJob.input / AgentJob.result の payload 形のみ定義する（スコープ・保存先・
// ライフサイクルは Layer1 のジョブレコードが担う）。
//
// TODO(shared-domain governance): LlmMetaSchema の正本が dev-lab 配下にあり product 横断 import に
// なっている。lib/llm-meta への昇格は別課題（idea-review と同じ相乗り）。
import { z } from "zod";
import { LlmMetaSchema } from "../dev-lab/llm-meta";

// ===== jobKind 定数（dispatcher は "inquiry-answer" prefix で routing） =====
export const INQUIRY_ANSWER_KIND = "inquiry-answer";

// ===== 問い合わせカテゴリ（入力補助 + プロンプトヒント + Slack/Admin 表示） =====
// fix3: 5 種に拡張。既存 3 値の id は保存済みデータ後方互換のため温存（ラベルのみ変更可）。
// enum 追加は ops worker の bundle 済み schema にも波及するため、新カテゴリ運用開始前に
// ops の再デプロイが必要（未デプロイだと feedback/other の AI ドラフトが validation failed）。
export const INQUIRY_CATEGORIES = [
  "howto",
  "bug",
  "request",
  "feedback",
  "other",
] as const;
export const InquiryCategorySchema = z.enum(INQUIRY_CATEGORIES);
export type InquiryCategory = z.infer<typeof InquiryCategorySchema>;

export const INQUIRY_CATEGORY_LABELS: Record<InquiryCategory, string> = {
  howto: "使い方がわからない",
  bug: "不具合報告",
  request: "要望",
  feedback: "感想",
  other: "その他",
};

// ===== jobKind=inquiry-answer の input payload =====
// 問い合わせ本文は小さい（件名100字+本文4000字）ため inline。ガイドカタログは input に積まず
// worker が build 時に bundle する（台帳 DDB item 400KB 肥大防止・改ざんクライアント対策）。
export const InquiryAnswerInputSchema = z.object({
  inquiryId: z.string(),
  category: InquiryCategorySchema,
  title: z.string().max(100),
  body: z.string().max(4000),
  // 「解決しない場合は新しく問い合わせる」経由時の元問い合わせ（文脈引き継ぎ用）。
  relatedInquiry: z
    .object({
      title: z.string().max(100),
      body: z.string().max(4000),
      answerText: z.string().max(4000).optional(),
    })
    .optional(),
  // 指示付き再生成（Admin 起点）のときのみ入る。現ドラフトを指示に従って改訂する。
  instruction: z.string().max(2000).optional(),
  currentDraft: z.string().max(4000).optional(),
});
export type InquiryAnswerInput = z.infer<typeof InquiryAnswerInputSchema>;

// ===== inquiry-answer の result payload（AgentJob.result に入る） =====
// references はガイド記事への構造化参照。URL 文字列を draftText に埋めず、web 側が
// /guide/article/{categoryId}/{articleId} を描画する（ハルシネーション URL 防止）。
export const InquiryAnswerReferenceSchema = z.object({
  categoryId: z.string().max(64),
  articleId: z.string().max(64),
  title: z.string().max(200),
});
export type InquiryAnswerReference = z.infer<
  typeof InquiryAnswerReferenceSchema
>;

export const InquiryAnswerResultSchema = z.object({
  inquiryId: z.string(), // read-back 時の宛先ガード（result.inquiryId === 対象 inquiry）
  draftText: z.string().max(4000),
  references: z.array(InquiryAnswerReferenceSchema).max(5).default([]),
  meta: LlmMetaSchema.optional(),
});
export type InquiryAnswerResult = z.infer<typeof InquiryAnswerResultSchema>;

// 再生成で同一問い合わせに複数ジョブが立つため、jobId は inquiryId + 世代番号で構成する。
export const inquiryAnswerJobId = (inquiryId: string, rev: number): string =>
  `${inquiryId}-r${rev}`;
